import { useEffect, useRef, useState } from "react";

import {
  type DepartmentSummary,
  type ManagedUser,
  type ResourceGrant,
  type ResourceType,
  type UserRole,
} from "../../api/client";
import { ProfileSection } from "./UserProfileSection";
import { UserGrantsSection } from "./UserGrantsSection";
import { UserLifecycleSection } from "./UserLifecycleSection";
import { UserSecuritySection } from "./UserSecuritySection";

interface UserDetailDrawerProps {
  actorRole: "manager" | "owner";
  departments: DepartmentSummary[];
  onClose: () => void;
  onUpdated: (user: ManagedUser) => void;
  user: ManagedUser;
}

export function UserDetailDrawer({
  actorRole,
  departments,
  onClose,
  onUpdated,
  user: initialUser,
}: UserDetailDrawerProps) {
  const [user, setUser] = useState(initialUser);
  const drawer = useRef<HTMLElement>(null);

  useEffect(() => {
    drawer.current?.querySelector<HTMLElement>("[data-initial-focus]")?.focus();
  }, []);

  function updateUser(updated: ManagedUser) {
    setUser(updated);
    onUpdated(updated);
  }

  return (
    <div className="drawer-backdrop" role="presentation">
      <aside
        ref={drawer}
        aria-labelledby="user-detail-title"
        aria-modal="true"
        className="user-detail-drawer"
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
        role="dialog"
      >
        <header className="user-detail-header">
          <div>
            <span className="eyebrow">可管理账户详情</span>
            <h2 id="user-detail-title">{user.display_name}</h2>
            <p>{user.username}</p>
          </div>
          <button
            data-initial-focus
            aria-label="关闭用户详情"
            className="text-button"
            onClick={onClose}
            type="button"
          >
            关闭
          </button>
        </header>
        <div className="user-detail-sections">
          <ProfileSection
            actorRole={actorRole}
            departments={departments}
            onUpdated={updateUser}
            user={user}
          />
          <UserGrantsSection user={user} />
          <UserSecuritySection actorRole={actorRole} onUpdated={updateUser} user={user} />
          <UserLifecycleSection onUpdated={updateUser} user={user} />
        </div>
      </aside>
    </div>
  );
}

export interface UserSectionProps {
  onUpdated: (user: ManagedUser) => void;
  user: ManagedUser;
}

export interface GrantDraft {
  resourceId: string;
  resourceType: ResourceType;
}

export interface PendingGrantAction {
  grant?: ResourceGrant;
  kind: "add" | "revoke";
}

export interface ProfileDraft {
  departmentId: string;
  displayName: string;
  role: UserRole;
}
